import { unstable_useId as useId } from 'reakit';
import type { ComponentPropsWithoutRef, MouseEvent } from 'react';
import { forwardRef, useContext, useMemo } from 'react';
import { Search } from '@mos-cat/ds-icons';
import { useRegisterRow } from '../internal/useRegisterRow';
import { Box } from '../../Box/Box';
import { Text } from '../../Text/Text';
import { Row } from '../internal/Row';
import type { OptionParams } from '../internal/types';
import { searchContext } from '../internal/context';

type TextOptionProps = Omit<ComponentPropsWithoutRef<'div'>, 'role'> & {
  value: string;
};

export const TextOption = forwardRef<HTMLDivElement, TextOptionProps>(
  function TextOption({ children, value, ...props }, ref) {
    let { id } = useId(props) as { id: string };
    const cellIds = useMemo<OptionParams['cellIds']>(
      function getCellIds() {
        return [`${id}-content`];
      },
      [id]
    );
    const { send } = useContext(searchContext);
    useRegisterRow({
      type: 'text',
      id,
      value,
      cellIds,
    });
    return (
      <Row
        ref={ref}
        {...props}
        id={id}
        onMouseUp={(event: MouseEvent<HTMLDivElement>) => {
          props.onMouseUp?.(event);
          if (event.defaultPrevented) return;
          event.preventDefault();
          event.stopPropagation();
          send({ type: 'select', id });
        }}
      >
        <Box p="sp-4">
          <Text color="gray-65">
            <Search />
          </Text>
        </Box>
        <Box role="gridcell">
          <Text as="button" textAlign="left" id={cellIds[0]}>
            {children}
          </Text>
        </Box>
      </Row>
    );
  }
);

TextOption.displayName = 'TextOption';
